import {
  LazyQueryResultTuple,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import {
  CalendarToday,
  Close,
  FilterList,
  OpenInNew,
  Refresh,
  Search,
  Sort as SortIcon,
  ViewColumn,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  GlobalStyles,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Row } from '@tanstack/react-table';
import { debounce, filter, find, includes, map, omit, some } from 'lodash';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { Pagination } from '../../components/pagination/pagination';
import { Button, type ButtonProps } from '../button/button';
import {
  NoResultsOverrideType,
  NoResultsText,
} from '../pagination-table/no-results-text';
import { PaginationResponse } from '../pagination-table/pagination-table';
import { Table } from '../table/table';
import { ColumnProps } from '../table/table-types';
import { HeaderCollapseToggle } from './header-collapse-toggle';
import { useCollapseOnScroll } from './use-collapse-on-scroll';
import {
  SearchPageBody,
  SearchPageCard,
  SearchPageFooter,
  SearchPageHeader,
  SearchPageToolbar,
} from './search-page-frame';
import { useSearchPageHeight } from './use-search-page-height';
import {
  findScrollParent,
  usePageScroll,
} from '../../hooks/use-page-scroll/use-page-scroll';
import { settleFitHeight } from '../../hooks/fit-height/fit-height';

export interface SearchFilter {
  label: string;
  value: string;
  type:
    | 'select'
    | 'multi-select'
    | 'date-range'
    | 'date-field-range'
    | 'checkbox'
    | 'custom';
  /**
   * For `multi-select`, options sharing a `group` value render under a
   * clickable group header that toggles every option in the group at once.
   * Options with no `group` render at the top without a header. The render
   * order of groups matches the first-seen order of `group` values in this
   * array, so callers should keep grouped options contiguous.
   */
  options?: { label: string; value: string; group?: string }[];
  isActive?: boolean;
  selectedValue?: string;
  selectedValues?: string[];
  /** ISO date string (YYYY-MM-DD). Used by date-range and date-field-range filters. Either bound is optional. */
  selectedDateFrom?: string;
  selectedDateTo?: string;
  /**
   * For `custom` filters: an opaque flat string map the caller reads/writes.
   * SearchPage stores it, serializes it to the URL, and hands it to
   * `renderControl` / `formatChip` — it never interprets the contents.
   */
  customValues?: Record<string, string>;
  /**
   * For `custom` filters: renders the popover body. Receives the buffered
   * values and a patch setter; keep app-specific pickers (that need app data)
   * here so the shared component stays data-agnostic.
   */
  renderControl?: (
    values: Record<string, string>,
    onChange: (patch: Record<string, string>) => void,
  ) => ReactNode;
  /**
   * For `custom` filters: turns the active values into the human-readable label
   * shown in the closed input and the active-filter chip. Return a falsy value
   * to treat the filter as empty.
   */
  formatChip?: (values: Record<string, string>) => string | undefined;
  /**
   * The `value` of another filter this one only means something alongside.
   * While that filter is inactive this one is hidden, and turning it off
   * clears this one too — so a modifier like "also match by head status" can
   * neither show as an active filter that changes nothing nor linger into a
   * later selection.
   */
  dependsOn?: string;
}

/**
 * Switch off every filter whose `dependsOn` filter is not active.
 * @param filters - filter state after a change
 * @returns the same state, minus filters left without what they depend on
 */
const clearOrphanedFilters = (filters: SearchFilter[]): SearchFilter[] =>
  filters.map((f) =>
    f.dependsOn &&
    f.isActive &&
    !filters.find((d) => d.value === f.dependsOn)?.isActive
      ? { ...f, isActive: false }
      : f,
  );

// Breakpoint order for comparison
const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

// Prefix for per-filter URL params, e.g. `f.status=active`, `f.date.from=2025-01-01`.
const FILTER_PARAM_PREFIX = 'f.';

const encodeFilterToParams = (
  params: URLSearchParams,
  f: SearchFilter,
): void => {
  if (!f.isActive) return;
  const key = `${FILTER_PARAM_PREFIX}${f.value}`;
  switch (f.type) {
    case 'select':
      if (f.selectedValue) params.set(key, f.selectedValue);
      break;
    case 'multi-select':
      if (f.selectedValues && f.selectedValues.length > 0) {
        params.set(key, f.selectedValues.join(','));
      }
      break;
    case 'date-range':
      if (f.selectedDateFrom) params.set(`${key}.from`, f.selectedDateFrom);
      if (f.selectedDateTo) params.set(`${key}.to`, f.selectedDateTo);
      break;
    case 'date-field-range':
      if (f.selectedValue) params.set(key, f.selectedValue);
      if (f.selectedDateFrom) params.set(`${key}.from`, f.selectedDateFrom);
      if (f.selectedDateTo) params.set(`${key}.to`, f.selectedDateTo);
      break;
    case 'checkbox':
      params.set(key, '1');
      break;
    case 'custom':
      if (f.customValues && Object.values(f.customValues).some(Boolean)) {
        params.set(key, JSON.stringify(f.customValues));
      }
      break;
  }
};

// A filter's URL form on its own — used to compare live state against what the
// active tab already implies.
const filterSignature = (f: SearchFilter): string => {
  const params = new URLSearchParams();
  encodeFilterToParams(params, f);
  return params.toString();
};

const encodeFiltersToParams = (
  params: URLSearchParams,
  searchFilters: SearchFilter[],
  /**
   * Filter state the selected tab implies, for the filters it drives. Anything
   * still matching it is left out of the URL — the `tab` param already says it,
   * and a preset of ten statuses makes for an unreadable link. Anything the tab
   * drives that the user has since turned OFF is written as an empty value,
   * which decodes back to "explicitly cleared" instead of silently falling back
   * to the preset on reload.
   */
  tabBaseline?: SearchFilter[],
): void => {
  for (const f of searchFilters) {
    const baseline = tabBaseline?.find((b) => b.value === f.value);
    if (baseline) {
      const signature = filterSignature(f);
      if (signature === filterSignature(baseline)) continue;
      if (!signature) {
        params.set(`${FILTER_PARAM_PREFIX}${f.value}`, '');
        continue;
      }
    }
    encodeFilterToParams(params, f);
  }
};

const decodeFiltersFromParams = (
  params: URLSearchParams,
  filterDefs: SearchFilter[],
): { filters: SearchFilter[]; hadAny: boolean } => {
  let hadAny = false;
  const next = filterDefs.map((f) => {
    const key = `${FILTER_PARAM_PREFIX}${f.value}`;
    const raw = params.get(key);
    const fromRaw = params.get(`${key}.from`);
    const toRaw = params.get(`${key}.to`);

    if (raw == null && fromRaw == null && toRaw == null) {
      return f;
    }
    hadAny = true;

    const updated: SearchFilter = { ...f };
    switch (f.type) {
      case 'select':
        updated.selectedValue = raw || undefined;
        updated.isActive = !!raw;
        break;
      case 'multi-select':
        updated.selectedValues = raw ? raw.split(',').filter(Boolean) : [];
        updated.isActive = (updated.selectedValues?.length ?? 0) > 0;
        break;
      case 'date-range':
        updated.selectedDateFrom = fromRaw || undefined;
        updated.selectedDateTo = toRaw || undefined;
        updated.isActive = !!fromRaw || !!toRaw;
        break;
      case 'date-field-range':
        updated.selectedValue = raw || undefined;
        updated.selectedDateFrom = fromRaw || undefined;
        updated.selectedDateTo = toRaw || undefined;
        updated.isActive = !!fromRaw || !!toRaw;
        break;
      case 'checkbox':
        updated.isActive = raw === '1';
        break;
      case 'custom':
        try {
          updated.customValues = raw ? JSON.parse(raw) : {};
        } catch {
          updated.customValues = {};
        }
        updated.isActive = Object.values(updated.customValues ?? {}).some(
          Boolean,
        );
        break;
    }
    return updated;
  });
  return { filters: next, hadAny };
};

// Param holding the selected tab, e.g. `tab=needs-review`.
const TAB_PARAM = 'tab';

/**
 * Written to the `tab` param when the user lands on a filter set no tab
 * describes (they cleared the filters), so a reload doesn't silently re-apply
 * the default tab. Reserved — don't name a tab `none`.
 */
const NO_TAB = 'none';

/**
 * The slice of a filter a tab can preselect. Callers set only the fields the
 * filter's `type` uses (`selectedValues` for `multi-select`, `isActive` for
 * `checkbox`, and so on); `isActive` is derived from the values when omitted.
 */
export type SearchTabFilterPatch = Pick<
  SearchFilter,
  | 'isActive'
  | 'selectedValue'
  | 'selectedValues'
  | 'selectedDateFrom'
  | 'selectedDateTo'
  | 'customValues'
>;

export interface SearchPageTab {
  /** Stable id, serialized to the `tab` URL param. */
  value: string;
  label: string;
  /** Rendered before the label. */
  icon?: ReactElement;
  /** Rendered after the label — typically a count. */
  badge?: ReactNode;
  /**
   * Filter values this tab preselects, keyed by the filter's `value`. Selecting
   * a tab resets every tab-driven filter and applies these; filters no tab
   * mentions keep whatever the user set. A preselected filter stays editable —
   * a tab is a starting point, not a lock.
   */
  filters?: Record<string, SearchTabFilterPatch | undefined>;
}

const isPatchActive = (patch: SearchTabFilterPatch): boolean =>
  !!patch.selectedValue ||
  (patch.selectedValues?.length ?? 0) > 0 ||
  !!patch.selectedDateFrom ||
  !!patch.selectedDateTo ||
  Object.values(patch.customValues ?? {}).some(Boolean);

// Fresh filter state, before any tab or URL param is applied.
const buildBaseFilters = (
  filterDefs: SearchFilter[],
  defaultFilterValues: string[],
): SearchFilter[] =>
  map(filterDefs, (f) => ({
    ...f,
    isActive:
      includes(defaultFilterValues, f.value) ||
      (!!f.selectedValues && f.selectedValues.length > 0) ||
      !!f.selectedValue,
  }));

/**
 * Strip every value off a filter, leaving the definition (label, type, options,
 * renderers) intact.
 *
 * This — not the filter's base state — is what a tab resets to. `buildBaseFilters`
 * marks a filter active when its own *definition* carries a `selectedValue` /
 * `selectedValues`, which is right for a page's initial paint but wrong as a
 * reset target: resetting to it re-applies the definition's preselection, so a
 * tab with no `filters` map ("All") could never clear a filter that declares a
 * default. Questionnaire Responses and the scheduling waitlist both did exactly
 * that — their "All" tabs silently kept showing only Vendor responses and only
 * Active entries.
 *
 * @param f - The filter to clear.
 * @returns The same filter with no values and inactive.
 */
const clearFilterValues = (f: SearchFilter): SearchFilter => ({
  ...f,
  isActive: false,
  selectedValue: undefined,
  selectedValues: undefined,
  selectedDateFrom: undefined,
  selectedDateTo: undefined,
  customValues: undefined,
});

/**
 * Apply a tab's preselected values on top of `current`. Filters owned by any
 * tab (`tabKeys`) are cleared first, so switching tabs never leaves the previous
 * tab's values — or the filter definition's own default — behind; everything
 * else is left alone.
 */
const applyTabFilters = (
  current: SearchFilter[],
  tab: SearchPageTab | undefined,
  tabKeys: Set<string>,
): SearchFilter[] =>
  current.map((f) => {
    if (!tabKeys.has(f.value)) return f;
    const cleared = clearFilterValues(f);
    const patch = tab?.filters?.[f.value];
    if (!patch) return cleared;
    return {
      ...cleared,
      ...patch,
      isActive: patch.isActive ?? isPatchActive(patch),
    };
  });

/**
 * One-line description of an active filter — "Status: 3 selected",
 * "Date: 2026-09-01 – 2026-09-30". Shown in the filter button's tooltip, which
 * is where the active-filter summary lives.
 */
const describeActiveFilter = (f: SearchFilter): string => {
  const optionLabel = (val: string) =>
    f.options?.find((o) => o.value === val)?.label ?? val;

  switch (f.type) {
    case 'checkbox':
      // A checkbox is on/off — its name already says everything.
      return f.label;
    case 'multi-select': {
      const vals = f.selectedValues ?? [];
      if (vals.length === 0) return f.label;
      if (vals.length === 1) return `${f.label}: ${optionLabel(vals[0])}`;
      return `${f.label}: ${vals.length} selected`;
    }
    case 'date-range':
    case 'date-field-range': {
      const parts = [f.selectedDateFrom, f.selectedDateTo].filter(Boolean);
      return parts.length ? `${f.label}: ${parts.join(' – ')}` : f.label;
    }
    case 'custom':
      return f.formatChip?.(f.customValues ?? {}) || f.label;
    default:
      return f.selectedValue
        ? `${f.label}: ${optionLabel(f.selectedValue)}`
        : f.label;
  }
};

// Helper function to get visible columns based on current breakpoint
const getVisibleColumnsByBreakpoint = <
  TData,
  TQuery,
  TVars extends OperationVariables,
>(
  columns: ColumnProps<TData, TQuery, TVars>[],
  currentBreakpoint: string,
): string[] => {
  const currentBreakpointIndex = BREAKPOINT_ORDER.indexOf(
    currentBreakpoint as (typeof BREAKPOINT_ORDER)[number],
  );

  return columns
    .filter((column) => {
      if (!column.label) return false;
      if (!column.minimumBreakpoint) return true;
      const minBreakpointIndex = BREAKPOINT_ORDER.indexOf(
        column.minimumBreakpoint,
      );
      return currentBreakpointIndex >= minBreakpointIndex;
    })
    .map((column) => column.label!)
    .filter(Boolean);
};

function DateFieldRangeFilter({
  filterItem,
  selectedFilter,
  onChange,
}: {
  filterItem: SearchFilter;
  selectedFilter: SearchFilter | undefined;
  onChange: (next: Partial<SearchFilter>) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const options = filterItem.options || [];

  // Applied (committed) values, used to render the closed text input
  const appliedField = selectedFilter?.selectedValue || options[0]?.value || '';
  const appliedFrom = selectedFilter?.selectedDateFrom || '';
  const appliedTo = selectedFilter?.selectedDateTo || '';

  // Buffered (in-popover) values — only flushed to onChange on close/apply
  const [bufField, setBufField] = useState(appliedField);
  const [bufFrom, setBufFrom] = useState(appliedFrom);
  const [bufTo, setBufTo] = useState(appliedTo);

  const fieldLabel =
    options.find((o) => o.value === appliedField)?.label || filterItem.label;

  const displayValue = (() => {
    if (!selectedFilter?.isActive) return '';
    if (appliedFrom && appliedTo)
      return `${fieldLabel}: ${appliedFrom} → ${appliedTo}`;
    if (appliedFrom) return `${fieldLabel}: from ${appliedFrom}`;
    if (appliedTo) return `${fieldLabel}: until ${appliedTo}`;
    return '';
  })();

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setBufField(appliedField);
    setBufFrom(appliedFrom);
    setBufTo(appliedTo);
    setAnchorEl(e.currentTarget);
  };

  const flush = (field: string, from: string, to: string) => {
    const sameAsApplied =
      field === appliedField && from === appliedFrom && to === appliedTo;
    if (sameAsApplied) return;
    onChange({
      isActive: !!from || !!to,
      selectedValue: field || undefined,
      selectedDateFrom: from || undefined,
      selectedDateTo: to || undefined,
    });
  };

  const handleClose = () => {
    flush(bufField, bufFrom, bufTo);
    setAnchorEl(null);
  };

  const clear = () => {
    setBufField(options[0]?.value || '');
    setBufFrom('');
    setBufTo('');
    onChange({
      isActive: false,
      selectedValue: undefined,
      selectedDateFrom: undefined,
      selectedDateTo: undefined,
    });
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <TextField
        size="small"
        fullWidth
        label={filterItem.label}
        value={displayValue}
        placeholder="Any date"
        onClick={openPopover}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarToday fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: displayValue ? (
              <InputAdornment position="end">
                <Tooltip title="Clear date filter">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clear();
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
            sx: { cursor: 'pointer' },
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Stack spacing={2}>
            {options.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel>Date field</InputLabel>
                <Select
                  value={bufField}
                  label="Date field"
                  onChange={(e) => setBufField(e.target.value as string)}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Stack direction="row" spacing={1}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="From"
                slotProps={{ inputLabel: { shrink: true } }}
                value={bufFrom}
                onChange={(e) => setBufFrom(e.target.value)}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="To"
                slotProps={{ inputLabel: { shrink: true } }}
                value={bufTo}
                onChange={(e) => setBufTo(e.target.value)}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                content="Clear"
                variant="text"
                size="sm"
                onClick={clear}
              />
              <Button content="Apply" size="sm" onClick={handleClose} />
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}

function CustomFilter({
  filterItem,
  selectedFilter,
  onChange,
}: {
  filterItem: SearchFilter;
  selectedFilter: SearchFilter | undefined;
  onChange: (next: Partial<SearchFilter>) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const applied = useMemo(
    () => selectedFilter?.customValues ?? {},
    [selectedFilter?.customValues],
  );

  // Buffered (in-popover) values — only flushed to onChange on close/apply.
  const [buf, setBuf] = useState<Record<string, string>>(applied);

  const displayValue =
    (selectedFilter?.isActive && filterItem.formatChip?.(applied)) || '';

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setBuf(applied);
    setAnchorEl(e.currentTarget);
  };

  const flush = (values: Record<string, string>) => {
    onChange({
      isActive: Object.values(values).some(Boolean),
      customValues: values,
    });
  };

  const handleClose = () => {
    flush(buf);
    setAnchorEl(null);
  };

  const clear = () => {
    setBuf({});
    onChange({ isActive: false, customValues: {} });
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <TextField
        size="small"
        fullWidth
        label={filterItem.label}
        value={displayValue}
        placeholder="Any"
        onClick={openPopover}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            readOnly: true,
            endAdornment: displayValue ? (
              <InputAdornment position="end">
                <Tooltip title={`Clear ${filterItem.label} filter`}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clear();
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
            sx: { cursor: 'pointer' },
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Stack spacing={2}>
            {filterItem.renderControl?.(buf, (patch) =>
              setBuf((prev) => ({ ...prev, ...patch })),
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                content="Clear"
                variant="text"
                size="sm"
                onClick={clear}
              />
              <Button content="Apply" size="sm" onClick={handleClose} />
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}

export interface SearchPageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export interface SearchPageProps<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
> {
  // Header
  header?: SearchPageHeaderProps;

  /**
   * Optional tabs rendered above the search bar. Selecting a tab preselects the
   * filter values it declares and stores the tab in the `tab` URL param, so the
   * view is restored on reload / back-navigation.
   */
  tabs?: SearchPageTab[];
  /**
   * Tab selected when the URL carries no valid `tab` param. Defaults to the
   * first tab.
   */
  defaultTab?: string;

  /**
   * Resolve how many results the free-text term alone would match, ignoring the
   * active filters. Called only while there is a search term AND at least one
   * active filter, and re-called only when the term changes. When the answer
   * beats the filtered count, SearchPage offers to drop the filters and show
   * them all. Keep the query count-only — the result is never rendered as rows.
   */
  fetchUnfilteredCount?: (
    searchValue: string,
  ) => Promise<number | null | undefined> | number | null | undefined;

  // Table props
  actionButtons?: (t: QueryResult<TQuery, TVars>) => ButtonProps[];
  dataAccessor: (r: TQuery) => TAccessor | undefined | null;
  columns: ColumnProps<TData, TQuery, TVars>[];
  renderSubComponent?: (
    row: Row<TData>,
    operation?: QueryResult<TQuery, TVars>,
  ) => ReactNode;

  // Search and filtering props
  searchPlaceholder?: string;
  filters?: SearchFilter[];
  defaultFilters?: string[];
  sortOptions: {
    label: string;
    value: TSort;
    /** Group key for asc/desc pairs. Items sharing a `field` collapse into one
     * row in the sort dropdown with a direction toggle. Omit for legacy single-direction entries. */
    field?: string;
    direction?: 'asc' | 'desc';
  }[];
  defaultSort: TSort;

  // Query building
  buildQuery: (
    searchValue: string,
    selectedFilters: SearchFilter[],
    sort: TSort,
    page: number,
    perPage: number,
  ) => TVars | undefined;
  paginationQuery: LazyQueryResultTuple<TQuery, TVars>;

  // Preview
  /** Render a preview panel for the selected row. When provided, clicking a row opens a side panel. */
  renderPreview?: (row: TData, onClose: () => void) => ReactNode;
  /**
   * Mirror the open preview's row id into this URL param, and reopen from it
   * on load. Without it the preview is local state, so a detail view can't be
   * linked to, shared, or survive a refresh — and any existing deep link into
   * a page that moves onto this chassis silently stops opening anything.
   *
   * Row ids come from `getRowId`, the same function row selection uses.
   */
  previewParam?: string;
  /** Configure the preview panel header. Return title, href (open in new tab), and optional action button. */
  previewConfig?: (row: TData) => {
    title: string;
    subtitle?: string;
    status?: string;
    href?: string;
    action?: ReactNode;
    /**
     * Drop the padding around the preview content, for content that is itself
     * a full panel (its own header, tabs, footer). Padded, such a panel reads
     * as a card floating inside the preview rather than as the preview.
     */
    flush?: boolean;
  };

  // UI options
  hideButtons?: boolean;
  hideSort?: boolean;
  persistentFilters?: boolean;
  enableSearchParams?: boolean;
  noResultsOverride?: NoResultsOverrideType;
  debounceMs?: number;
  /**
   * Size the container to the space actually below it, measured at runtime,
   * instead of the default `100vh - nav` calc. Use on pages that render a header
   * (e.g. a PageTitle with breadcrumbs) above the SearchPage — the fixed calc
   * ignores that header and overflows the viewport. Off by default so existing
   * full-height pages are unaffected.
   */
  autoFitHeight?: boolean;
  /**
   * The least height `autoFitHeight` will settle on (default 240).
   *
   * The fit subtracts whatever shares the scroll container with this page. A
   * host that puts something tall below it — a customer's activity timeline —
   * leaves nothing to fit into, and the floor is then the page's whole height:
   * at 240 the toolbar took nearly all of it and the list was a one-row slot.
   * Such a host scrolls anyway, so it should ask for a workable height here.
   */
  autoFitMinHeight?: number;
  /**
   * Render the rows as bare cards: no column header, no zebra striping.
   *
   * For the pages whose single column IS a card — the job, customer and
   * animal lists on a phone. There are no columns to align, so the header
   * is a label over nothing, and the striping draws a grey band behind
   * every other card that reads as a selected state.
   */
  cardRows?: boolean;
  /**
   * With `cardRows`: lay the cards out several to a row, each column at
   * least this many pixels wide, instead of one full-width card per row.
   *
   * A card built for a 370px grid cell stretched across a 1400px list is
   * mostly empty middle, and shows four records where it could show twelve.
   * Below this width — a phone — it is one column, as before.
   *
   * Ignored with `selectable` or `renderSubComponent`: the grid assumes one
   * cell per row, and a checkbox cell or a sub-row would become a card of
   * its own.
   */
  cardGridMinWidth?: number;

  // Row selection + batch actions
  /** Enable a leading checkbox column and multi-row selection. */
  selectable?: boolean;
  /** Derive a stable row id for selection; defaults to `row._id`. */
  getRowId?: (row: TData) => string;
  /**
   * Render a batch-action bar shown above the results while rows are selected.
   * Receives the currently selected rows (from the visible page) and a
   * `clearSelection` callback. Selection resets on search/sort/filter/page change.
   */
  renderSelectionActions?: (
    selectedRows: TData[],
    clearSelection: () => void,
  ) => ReactNode;
  /** When provided, only rows returning true get a selection checkbox. */
  isRowSelectable?: (row: TData) => boolean;
}

export function SearchPage<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
>({
  header,
  tabs = [],
  defaultTab,
  fetchUnfilteredCount,
  actionButtons = undefined,
  dataAccessor,
  buildQuery,
  columns,
  defaultFilters = [],
  defaultSort,
  filters = [],
  paginationQuery,
  renderSubComponent = undefined,
  renderPreview,
  previewParam = undefined,
  previewConfig,
  hideButtons = false,
  hideSort = false,
  persistentFilters = false,
  enableSearchParams = true,
  noResultsOverride = undefined,
  searchPlaceholder = 'Search...',
  sortOptions,
  debounceMs = 300,
  selectable = false,
  getRowId = (row: TData) => (row as { _id?: string })?._id ?? '',
  renderSelectionActions,
  isRowSelectable,
  autoFitHeight = false,
  autoFitMinHeight = 240,
  cardRows = false,
  cardGridMinWidth = undefined,
}: SearchPageProps<TData, TAccessor, TQuery, TVars, TSort>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Sized so the container, its top margin and the page's bottom padding fit
  // the viewport exactly.
  const containerHeight = useSearchPageHeight();

  // When autoFitHeight is on, size the container to the space actually left
  // below its top edge (which accounts for any header rendered above it),
  // instead of the fixed 100vh calc that overflows on such pages.
  const containerRef = useRef<HTMLDivElement>(null);

  // On a phone the document scrolls rather than the list, and the search bar,
  // tabs and footer pin to the screen — see `SearchPageCardProps.pageScroll`.
  // Not inside a host that scrolls its own panel (TabbedContainer, say): the
  // document doesn't move there, so the card keeps its fixed height.
  const pageScroll = usePageScroll(containerRef);
  const [fittedHeight, setFittedHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!autoFitHeight) return undefined;

    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const scrollParent = findScrollParent(el);

      let available: number;
      if (!scrollParent) {
        // Owning the viewport: 16px breathing room at the bottom.
        //
        // Where the container sits in the document, not on screen. Under page
        // scroll this re-runs mid-scroll (iOS fires `resize` as its toolbar
        // tucks away), and the on-screen top is negative by then — the fit
        // came out as tall as the list and stuck as the minimum height.
        const top = el.getBoundingClientRect().top + window.scrollY;
        available = window.innerHeight - top - 16;
      } else {
        // Inside a scrolling shell, fit the container's content box — and
        // account for whatever sits BELOW us in it (section margins, the
        // card's bottom padding, the tab panel's). Subtracting a flat 16px
        // left that padding unaccounted for, so the page ended up slightly
        // taller than the box and overscrolled by the difference.
        //
        // Measured with the box as tall as the scroller, so the content always
        // overflows and `scrollHeight` is its real height. At its fitted
        // height a shorter list leaves `scrollHeight` clamped to
        // `clientHeight`, the trailing space soaked up all the spare room, and
        // the fit could never grow. Put back before the browser paints.
        const inlineHeight = el.style.height;
        el.style.height = `${scrollParent.clientHeight}px`;
        const elRect = el.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const scrolled = scrollParent.scrollTop;
        const topInContent = elRect.top - parentRect.top + scrolled;
        const bottomInContent = elRect.bottom - parentRect.top + scrolled;
        const { scrollHeight } = scrollParent;
        el.style.height = inlineHeight;

        // Rounded up: `scrollHeight` is a whole pixel and the rects aren't, so
        // the content can end up to half a pixel past it.
        const trailing = Math.max(0, Math.ceil(scrollHeight - bottomInContent));
        available =
          scrollParent.clientHeight - Math.ceil(topInContent) - trailing;
      }

      setFittedHeight((current) =>
        settleFitHeight(available, autoFitMinHeight, current),
      );
    };

    measure();
    window.addEventListener('resize', measure);

    // The scroll container can change size without the window doing so — a tab
    // switch, or the shell's own layout settling after mount.
    const el = containerRef.current;
    const scrollParent = el ? findScrollParent(el) : null;
    const observer = scrollParent ? new ResizeObserver(measure) : null;
    if (scrollParent && observer) observer.observe(scrollParent);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [autoFitHeight, autoFitMinHeight]);

  const resolvedHeight =
    autoFitHeight && fittedHeight !== null
      ? `${fittedHeight}px`
      : containerHeight;

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  const currentBreakpoint = useMemo(() => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    if (isXl) return 'xl';
    return 'xxl';
  }, [isXs, isSm, isMd, isLg, isXl]);

  const initialVisibleColumns = useMemo(
    () => getVisibleColumnsByBreakpoint(columns, currentBreakpoint),
    [columns, currentBreakpoint],
  );

  // Group asc/desc pairs by `field` so the dropdown shows one entry per field
  // with a separate direction toggle button.
  const sortGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        ascValue?: TSort;
        descValue?: TSort;
        values: TSort[];
      }
    >();
    for (const opt of sortOptions) {
      const key = opt.field ?? `__legacy_${String(opt.value)}`;
      const existing = groups.get(key) ?? {
        key,
        label: opt.label,
        values: [] as TSort[],
      };
      existing.values.push(opt.value);
      if (opt.direction === 'asc') existing.ascValue = opt.value;
      else if (opt.direction === 'desc') existing.descValue = opt.value;
      if (opt.direction === 'asc' || !existing.label)
        existing.label = opt.label;
      groups.set(key, existing);
    }
    return Array.from(groups.values());
  }, [sortOptions]);

  // Search state
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentSort, setCurrentSort] = useState<TSort>(defaultSort);
  const [showFilters, setShowFilters] = useState<boolean>(!isMobile);
  const [selectedColumns, setSelectedColumns] = useState(initialVisibleColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(15);
  const [pageCount, setPageCount] = useState<number>(1);

  // Preview state
  const [selectedRow, setSelectedRow] = useState<TData | null>(null);
  const [previewKey, setPreviewKey] = useState(0);

  // Where the list was scrolled to when a preview took the page from it.
  const listScrollRef = useRef<number | null>(null);

  const handleSelectRow = useCallback((row: TData) => {
    setSelectedRow((prev) => {
      // Read before the list is hidden, which clamps the document's offset.
      if (!prev) listScrollRef.current = window.scrollY;
      return row;
    });
    setPreviewKey((k) => k + 1);
  }, []);

  /**
   * Under page scroll, bring the top of this page under whatever is pinned
   * above it. Not `window.scrollTo(0)`: as a tab of a detail page that is the
   * record's header, a screen or more above the list.
   */
  const scrollPageToTop = useCallback(() => {
    // Optional call: jsdom has no scrollIntoView.
    containerRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  /**
   * Open/close the preview, keeping `previewParam` in step when one is set.
   * `replace`, like every other param this chassis writes: stepping back
   * through each row you opened is not what Back is for.
   */
  const setPreviewRow = useCallback(
    (row: TData | null) => {
      if (row) handleSelectRow(row);
      else setSelectedRow(null);
      if (!previewParam) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (row) next.set(previewParam, getRowId(row));
          else next.delete(previewParam);
          return next;
        },
        { replace: true },
      );
    },
    [handleSelectRow, previewParam, setSearchParams, getRowId],
  );

  // Row selection state (per visible page; reset on any query change below).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleRowSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllSelection = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Tab state — the union of filter keys any tab drives, so switching tabs can
  // reset exactly those and leave the user's own filters alone.
  const tabKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const t of tabs) {
      for (const key of Object.keys(t.filters ?? {})) keys.add(key);
    }
    return keys;
  }, [tabs]);

  const [activeTab, setActiveTab] = useState<string | undefined>(() => {
    if (!tabs.length) return undefined;
    const fromUrl = enableSearchParams ? searchParams.get(TAB_PARAM) : null;
    if (fromUrl === NO_TAB) return undefined;
    if (fromUrl && tabs.some((t) => t.value === fromUrl)) return fromUrl;
    if (defaultTab && tabs.some((t) => t.value === defaultTab)) {
      return defaultTab;
    }
    return tabs[0].value;
  });

  // performSearch writes the tab into the URL. It reads the ref so callers that
  // change the tab and search in the same tick can pass the new value directly.
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // performSearch carries the open preview's param across its URL rewrite. It
  // reads the live params from a ref because a debounced search holds an
  // older performSearch for up to `debounceMs`: the params that one captured
  // still name a preview the user has since closed, and writing them back
  // reopened it. (A functional `setSearchParams` would not do — this router's
  // `prev` is the params its own callback closed over, just as stale.)
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  // Filter state — seeded with the initial tab's preselection so the first
  // paint already reflects it. URL filter params are layered on top on mount.
  const [selectedFilters, setSelectedFilters] = useState<SearchFilter[]>(() =>
    applyTabFilters(
      buildBaseFilters(filters, defaultFilters),
      tabs.find((t) => t.value === activeTab),
      tabKeys,
    ),
  );

  const [loadData, operation] = paginationQuery;

  const accessor = useMemo(
    () => operation.data && dataAccessor(operation.data),
    [dataAccessor, operation.data],
  );

  // Under page scroll the header scrolls away with the page, like any other.
  const headerCollapse = useCollapseOnScroll(isMobile && !pageScroll);

  // On a phone the sort dropdown is folded into the filter panel so the search
  // box and the icon buttons fit on one row.
  const sortInPanel = isMobile && !hideSort;
  // Refresh goes with it. It is the only way to re-run the same view — a
  // search, filter or page change refetches only because the variables change
  // — so on a phone it moves rather than disappears.
  const refreshInPanel = isMobile;
  // ...and that panel is a popover rather than an inline section. That
  // includes pages that ask for `persistentFilters`: keeping filters
  // permanently open is a wide-screen nicety, and on a phone it cost most of
  // the screen.
  const filtersInPopover = isMobile;
  // The inline panel starts open and the popover starts closed, and they share
  // `showFilters`. Reset it when the screen crosses from one to the other —
  // a rotated tablet, a resized window — or the inline panel's "open" carried
  // over and the popover came up over the list unasked. Done while rendering
  // rather than in an effect, so the popover never gets a frame to open in.
  const [filtersWereInPopover, setFiltersWereInPopover] =
    useState(filtersInPopover);
  if (filtersWereInPopover !== filtersInPopover) {
    setFiltersWereInPopover(filtersInPopover);
    setShowFilters(!filtersInPopover);
  }
  const showFilterButton =
    (filters.length > 0 || sortInPanel || refreshInPanel) &&
    (!persistentFilters || isMobile);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  let filterButtonLabel = 'Filters';
  if (sortInPanel) {
    filterButtonLabel = filters.length > 0 ? 'Sort and filters' : 'Sort';
  } else if (refreshInPanel && filters.length === 0) {
    filterButtonLabel = 'Options';
  }

  // Reopen the preview from the URL. Runs whenever the rows change, not just
  // on mount: the param is read before the first page has loaded, and a row
  // can only be matched once it exists. Guarded on the id actually differing
  // so it never fights a user who just closed the panel.
  const previewId = previewParam ? searchParams.get(previewParam) : null;
  useEffect(() => {
    if (!previewParam || !previewId || !renderPreview) return;
    if (selectedRow && getRowId(selectedRow) === previewId) return;
    const match = (accessor?.items ?? []).find(
      (row) => row && getRowId(row) === previewId,
    );
    if (match) handleSelectRow(match);
  }, [
    previewParam,
    previewId,
    renderPreview,
    selectedRow,
    accessor?.items,
    getRowId,
    handleSelectRow,
  ]);

  // Keep the open preview on the latest copy of its row. `selectedRow` is the
  // row as it was when clicked; without this, a save made inside the panel
  // refetches the list but the panel keeps showing the old row until a reload
  // (PRO-798). Keyed on the query's data, not the rows, so a `dataAccessor`
  // that rebuilds its rows every render can't loop. Not `handleSelectRow`:
  // that remounts the panel, losing its tab and any unsaved edits.
  const lastPreviewDataRef = useRef(operation.data);
  useEffect(() => {
    if (operation.data === lastPreviewDataRef.current) return;
    lastPreviewDataRef.current = operation.data;
    setSelectedRow((prev) => {
      if (!prev) return prev;
      const id = getRowId(prev);
      const fresh = (accessor?.items ?? []).find(
        (row) => row && getRowId(row) === id,
      );
      return fresh ?? prev;
    });
  }, [operation.data, accessor, getRowId]);

  const currentGroup = useMemo(
    () => sortGroups.find((g) => g.values.includes(currentSort)),
    [sortGroups, currentSort],
  );

  const currentDirection: 'asc' | 'desc' | undefined =
    currentGroup?.ascValue === currentSort
      ? 'asc'
      : currentGroup?.descValue === currentSort
        ? 'desc'
        : undefined;

  const canToggleDirection =
    !!currentGroup?.ascValue && !!currentGroup?.descValue;

  // "N results without filters" prompt. The count depends only on the search
  // term (it ignores filters by definition), so it's cached per term and
  // dropped as soon as the term clears or the last filter comes off.
  const [unfilteredCount, setUnfilteredCount] = useState<number | null>(null);
  const countRequestRef = useRef(0);
  const countedTermRef = useRef<string | null>(null);

  const refreshUnfilteredCount = useCallback(
    (searchTerm: string, searchFilters: SearchFilter[]) => {
      if (!fetchUnfilteredCount) return;
      const hasActiveFilter = searchFilters.some((f) => f.isActive);

      if (!searchTerm || !hasActiveFilter) {
        countedTermRef.current = null;
        // Bump the id so an in-flight response for the old term is ignored.
        countRequestRef.current += 1;
        setUnfilteredCount(null);
        return;
      }

      if (countedTermRef.current === searchTerm) return;
      countedTermRef.current = searchTerm;

      countRequestRef.current += 1;
      const requestId = countRequestRef.current;
      Promise.resolve(fetchUnfilteredCount(searchTerm))
        .then((count) => {
          if (requestId !== countRequestRef.current) return;
          setUnfilteredCount(typeof count === 'number' ? count : null);
        })
        .catch(() => {
          if (requestId !== countRequestRef.current) return;
          // A failed count is a missing hint, not a page error — stay quiet.
          countedTermRef.current = null;
          setUnfilteredCount(null);
        });
    },
    [fetchUnfilteredCount],
  );

  /**
   * The filter state a tab implies, limited to the filters it drives — what the
   * URL encoder diffs against so a selected tab doesn't spell its whole preset
   * out in the query string.
   */
  const tabBaselineFor = useCallback(
    (tabValue: string | undefined): SearchFilter[] | undefined => {
      if (!tabKeys.size) return undefined;
      return applyTabFilters(
        buildBaseFilters(filters, defaultFilters),
        tabs.find((t) => t.value === tabValue),
        tabKeys,
      ).filter((f) => tabKeys.has(f.value));
    },
    [tabKeys, filters, defaultFilters, tabs],
  );

  const performSearch = useCallback(
    (
      searchTerm: string,
      searchFilters: SearchFilter[],
      sort: TSort,
      pageNum: number,
      tabValue: string | undefined = activeTabRef.current,
    ) => {
      const variables = buildQuery(
        searchTerm,
        searchFilters,
        sort,
        pageNum,
        perPage,
      );
      if (variables) {
        loadData({ variables });
        setPage(pageNum);
        // Selection is scoped to the visible page; drop it on any query change.
        setSelectedIds(new Set());
        refreshUnfilteredCount(searchTerm, searchFilters);

        // Mirror search state into URL params. We use { replace: true } so each
        // keystroke / filter tweak doesn't add a history entry — the back button
        // then leaves the page entirely rather than walking through prior search
        // states, while forward-nav from a detail page still restores results.
        if (enableSearchParams) {
          const newParams = new URLSearchParams();
          if (tabValue) newParams.set(TAB_PARAM, tabValue);
          else if (tabs.length) newParams.set(TAB_PARAM, NO_TAB);
          if (searchTerm) newParams.set('search', searchTerm);
          if (pageNum > 1) newParams.set('page', pageNum.toString());
          if (String(sort) !== String(defaultSort)) {
            newParams.set('sort', String(sort));
          }
          encodeFiltersToParams(
            newParams,
            searchFilters,
            tabBaselineFor(tabValue),
          );
          // The URL is rebuilt from scratch above, which is right for search
          // state and wrong for the open preview: it is not search state, and
          // dropping it here deleted a `?animal=…` deep link on mount, before
          // the first page had loaded a row to reopen it against.
          const openPreviewId = previewParam
            ? searchParamsRef.current.get(previewParam)
            : null;
          if (previewParam && openPreviewId) {
            newParams.set(previewParam, openPreviewId);
          }
          setSearchParams(newParams, { replace: true });
        }
      }
    },
    [
      buildQuery,
      loadData,
      perPage,
      enableSearchParams,
      setSearchParams,
      previewParam,
      defaultSort,
      tabs.length,
      tabBaselineFor,
      refreshUnfilteredCount,
    ],
  );

  const debouncedSearch = useRef(
    debounce((searchTerm: string) => {
      performSearch(searchTerm, selectedFilters, currentSort, 1);
    }, debounceMs),
  );

  useEffect(() => {
    debouncedSearch.current = debounce((searchTerm: string) => {
      performSearch(searchTerm, selectedFilters, currentSort, 1);
    }, debounceMs);
  }, [selectedFilters, currentSort, debounceMs, performSearch]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    debouncedSearch.current(value);
  }, []);

  const handleSortChange = useCallback(
    (newSort: TSort) => {
      setCurrentSort(newSort);
      performSearch(searchValue, selectedFilters, newSort, 1);
    },
    [searchValue, selectedFilters, performSearch],
  );

  const handleSortGroupChange = useCallback(
    (groupKey: string) => {
      const group = sortGroups.find((g) => g.key === groupKey);
      if (!group) return;
      // preserve current direction if the new group supports it; otherwise pick whichever is available
      const next =
        (currentDirection === 'desc' ? group.descValue : group.ascValue) ??
        group.ascValue ??
        group.descValue ??
        group.values[0];
      if (next != null) handleSortChange(next as TSort);
    },
    [sortGroups, currentDirection, handleSortChange],
  );

  const handleDirectionToggle = useCallback(() => {
    if (!currentGroup || !canToggleDirection) return;
    const next =
      currentDirection === 'asc'
        ? currentGroup.descValue
        : currentGroup.ascValue;
    if (next != null) handleSortChange(next as TSort);
  }, [currentGroup, canToggleDirection, currentDirection, handleSortChange]);

  const handleFilterChange = useCallback(
    (changed: SearchFilter[]) => {
      const updatedFilters = clearOrphanedFilters(changed);
      setSelectedFilters(updatedFilters);
      performSearch(searchValue, updatedFilters, currentSort, 1);
    },
    [searchValue, currentSort, performSearch],
  );

  /**
   * The tab that means "no preset" — a tab whose `filters` map is empty, i.e.
   * an "All" tab. Clearing every filter lands here.
   *
   * `undefined` when the caller spells out a preset on every tab (Invoices and
   * Cutsheets do). There is then no tab that describes an unfiltered list, and
   * clearing would leave the strip unselected while `buildQuery` quietly
   * substitutes the page's own default — a search that says "without filters"
   * but is still filtered. `canClearToUnfiltered` below gates the prompt on
   * that, so the affordance is only offered where it can tell the truth.
   */
  const neutralTab = useMemo(
    () => tabs.find((t) => Object.keys(t.filters ?? {}).length === 0)?.value,
    [tabs],
  );

  // Drop every filter but keep the search term — what the "search without
  // filters" prompt does.
  const clearAllFilters = useCallback(() => {
    const cleared = map(selectedFilters, (f) => ({
      ...f,
      isActive: false,
      selectedValue: undefined,
      selectedValues: [],
      selectedDateFrom: undefined,
      selectedDateTo: undefined,
      customValues: {},
    }));
    setActiveTab(neutralTab);
    activeTabRef.current = neutralTab;
    setSelectedFilters(cleared);
    performSearch(searchValue, cleared, currentSort, 1, neutralTab);
  }, [selectedFilters, neutralTab, searchValue, currentSort, performSearch]);

  const handleTabChange = useCallback(
    (nextTab: string) => {
      if (nextTab === activeTab) return;
      const updatedFilters = clearOrphanedFilters(
        applyTabFilters(
          selectedFilters,
          tabs.find((t) => t.value === nextTab),
          tabKeys,
        ),
      );
      setActiveTab(nextTab);
      activeTabRef.current = nextTab;
      setSelectedFilters(updatedFilters);
      performSearch(searchValue, updatedFilters, currentSort, 1, nextTab);
    },
    [
      activeTab,
      tabs,
      tabKeys,
      selectedFilters,
      searchValue,
      currentSort,
      performSearch,
    ],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      performSearch(searchValue, selectedFilters, currentSort, newPage);
      // The pinned footer is at the bottom of the screen wherever the page is
      // scrolled to, so a new page of rows would otherwise open mid-list.
      if (pageScroll) scrollPageToTop();
    },
    [
      searchValue,
      selectedFilters,
      currentSort,
      performSearch,
      pageScroll,
      scrollPageToTop,
    ],
  );

  useEffect(() => {
    const visibleColumns = getVisibleColumnsByBreakpoint(
      columns,
      currentBreakpoint,
    );
    setSelectedColumns((prev) =>
      prev.filter((col) => visibleColumns.includes(col)),
    );
  }, [currentBreakpoint, columns]);

  useEffect(() => {
    if (accessor?.pageInfo) {
      setPageCount(accessor.pageInfo.pageCount || 1);
    }
  }, [accessor]);

  useEffect(() => {
    let initialSearchValue = '';
    let initialPage = 1;
    let initialFilters = selectedFilters;
    let initialSort: TSort = defaultSort;

    if (enableSearchParams) {
      const searchParam = searchParams.get('search') || '';
      const pageParam = parseInt(searchParams.get('page') || '1', 10);
      const sortParam = searchParams.get('sort');

      if (searchParam) {
        setSearchValue(searchParam);
        initialSearchValue = searchParam;
      }
      if (pageParam > 1) {
        setPage(pageParam);
        initialPage = pageParam;
      }
      if (sortParam) {
        const matched = sortOptions.find((o) => String(o.value) === sortParam);
        if (matched) {
          setCurrentSort(matched.value);
          initialSort = matched.value;
        }
      }

      const { filters: urlFilters, hadAny } = decodeFiltersFromParams(
        searchParams,
        selectedFilters,
      );
      if (hadAny) {
        setSelectedFilters(urlFilters);
        initialFilters = urlFilters;
      }
    }

    performSearch(initialSearchValue, initialFilters, initialSort, initialPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The URL as the page was opened. The mount search above rewrites the live
  // params from the filters it knows, which drops the param of any filter that
  // hasn't been defined yet — so a late one reads its value from here.
  const openingParamsRef = useRef(new URLSearchParams(searchParams));

  // Filter state is seeded once, but a page can define a filter only after its
  // options have loaded (the jobs page's Tags). Without this the control would
  // render from `filters` while every change mapped over a state list that
  // never heard of it — clicks that do nothing. A filter that goes away is
  // dropped the same way, so it can't keep narrowing the list unseen.
  useEffect(() => {
    const known = new Set(selectedFilters.map((f) => f.value));
    const defined = new Set(filters.map((f) => f.value));
    const added = filters.filter((f) => !known.has(f.value));
    const removed = selectedFilters.filter((f) => !defined.has(f.value));
    if (added.length === 0 && removed.length === 0) return;

    let incoming = applyTabFilters(
      buildBaseFilters(added, defaultFilters),
      tabs.find((t) => t.value === activeTabRef.current),
      tabKeys,
    );
    if (enableSearchParams) {
      incoming = decodeFiltersFromParams(
        openingParamsRef.current,
        incoming,
      ).filters;
    }

    // In definition order, so state reads the way the controls are laid out.
    const byValue = new Map(
      [...selectedFilters, ...incoming].map((f) => [f.value, f]),
    );
    const next = filters.flatMap((f) => byValue.get(f.value) ?? []);
    setSelectedFilters(next);

    // Only a change to what's actually applied needs a new search.
    if (some(incoming, 'isActive') || some(removed, 'isActive')) {
      performSearch(searchValue, next, currentSort, 1);
    }
    // Keyed on the definitions alone: this reconciles state TO them, and must
    // not re-run on the state it writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const activeFilterCount = useMemo(
    () => filter(selectedFilters, 'isActive').length,
    [selectedFilters],
  );

  // What's currently narrowing the results, one line per filter — the filter
  // button's tooltip carries this instead of a row of chips under the controls.
  const activeFilterSummary = useMemo(
    () => filter(selectedFilters, 'isActive').map(describeActiveFilter),
    [selectedFilters],
  );

  const cleanedColumns: ColumnProps<TData, TQuery, TVars>[] = useMemo(
    () => map(columns, (column) => omit(column, 'minimumBreakpoint')),
    [columns],
  );

  const filteredColumns: ColumnProps<TData, TQuery, TVars>[] = useMemo(
    () =>
      filter(cleanedColumns, (c) => {
        const isButtonColumn: boolean = !c.label;
        return (c.label && selectedColumns.includes(c.label)) || isButtonColumn;
      }),
    [cleanedColumns, selectedColumns],
  );

  const isFetching = operation.loading || !operation.called;
  const handleRefresh = useCallback(() => {
    performSearch(searchValue, selectedFilters, currentSort, page);
  }, [performSearch, searchValue, selectedFilters, currentSort, page]);

  const handleColumnMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setColumnMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleColumnMenuClose = useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleColumnToggle = useCallback((column: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(column)) {
        if (prev.length === 1) return prev;
        return prev.filter((col) => col !== column);
      }
      return [...prev, column];
    });
  }, []);

  // The sort dropdown and its direction toggle. Rendered in the controls row
  // on wide screens and inside the filter panel on a phone.
  const renderSortControl = (fullWidth: boolean) => (
    <>
      <FormControl
        size="small"
        sx={fullWidth ? { flex: 1, minWidth: 0 } : { minWidth: 140 }}
      >
        <InputLabel>Sort</InputLabel>
        <Select
          value={currentGroup?.key || ''}
          label="Sort"
          onChange={(e) => handleSortGroupChange(e.target.value as string)}
          sx={{ borderRadius: 1.5 }}
        >
          {map(sortGroups, (group) => (
            <MenuItem key={group.key} value={group.key}>
              {group.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip
        title={
          !canToggleDirection
            ? 'Only one sort direction available'
            : currentDirection === 'asc'
              ? 'Sort ascending — click to flip'
              : 'Sort descending — click to flip'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={handleDirectionToggle}
            disabled={!canToggleDirection}
          >
            <SortIcon
              fontSize="small"
              sx={{
                transform:
                  currentDirection === 'asc' ? 'scaleY(-1)' : 'scaleY(1)',
                transition: 'transform 0.15s ease',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const renderFilterBar = () => {
    if (!filters.length && !sortInPanel && !refreshInPanel) return null;

    const panel = (
      <Box
        sx={{
          display: 'grid',
          alignItems: 'start',
          ...(filtersInPopover
            ? {
                // `minmax(0, …)`, not `1fr`: a bare `1fr` column grows to fit
                // a select showing a long value and runs off the popover.
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 1.5,
              }
            : {
                mt: 1.5,
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 1,
              }),
        }}
      >
        {filters.map((filterItem) => {
          const selectedFilter = find(selectedFilters, {
            value: filterItem.value,
          });

          if (
            filterItem.dependsOn &&
            !find(selectedFilters, { value: filterItem.dependsOn })?.isActive
          ) {
            return null;
          }

          if (filterItem.type === 'select' && filterItem.options) {
            return (
              <Box key={filterItem.value}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{filterItem.label}</InputLabel>
                  <Select
                    value={
                      (selectedFilter?.isActive &&
                        selectedFilter?.selectedValue) ||
                      ''
                    }
                    label={filterItem.label}
                    onChange={(event) => {
                      const value = event.target.value as string;
                      const updatedFilters = map(selectedFilters, (f) =>
                        f.value === filterItem.value
                          ? {
                              ...f,
                              isActive: !!value,
                              selectedValue: value,
                            }
                          : f,
                      );
                      handleFilterChange(updatedFilters);
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {filterItem.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            );
          }

          if (filterItem.type === 'multi-select' && filterItem.options) {
            // Bucket options by `group` preserving first-seen order.
            // Options without a group go into a leading bucket with no
            // header so they render exactly like the legacy flat list.
            const buckets: {
              name?: string;
              options: { label: string; value: string }[];
            }[] = [];
            const bucketIndex = new Map<string | undefined, number>();
            for (const opt of filterItem.options) {
              const idx = bucketIndex.get(opt.group);
              if (idx == null) {
                bucketIndex.set(opt.group, buckets.length);
                buckets.push({ name: opt.group, options: [opt] });
              } else {
                buckets[idx].options.push(opt);
              }
            }

            // MUI's Select adds a clicked MenuItem's `value` to the
            // selection array. We prefix group-header values with this
            // marker so onChange can detect the click, expand it into a
            // whole-group toggle, and strip the marker before persisting.
            const GROUP_MARKER = '__group_toggle__';

            const currentValues = selectedFilter?.selectedValues || [];

            return (
              <Box key={filterItem.value}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{filterItem.label}</InputLabel>
                  <Select
                    multiple
                    value={currentValues}
                    label={filterItem.label}
                    renderValue={(selected) => {
                      return (selected as string[])
                        .map((value) => {
                          const option = filterItem.options?.find(
                            (opt) => opt.value === value,
                          );
                          return option?.label || value;
                        })
                        .join(', ');
                    }}
                    onChange={(event) => {
                      let values = event.target.value as string[];
                      const markerEntry = values.find((v) =>
                        v.startsWith(GROUP_MARKER),
                      );
                      if (markerEntry) {
                        const groupName = markerEntry.slice(
                          GROUP_MARKER.length,
                        );
                        const groupValues = (filterItem.options ?? [])
                          .filter((o) => o.group === groupName)
                          .map((o) => o.value);
                        const allWereSelected =
                          groupValues.length > 0 &&
                          groupValues.every((v) => currentValues.includes(v));
                        values = values.filter(
                          (v) => !v.startsWith(GROUP_MARKER),
                        );
                        if (allWereSelected) {
                          values = values.filter(
                            (v) => !groupValues.includes(v),
                          );
                        } else {
                          values = Array.from(
                            new Set([...values, ...groupValues]),
                          );
                        }
                      }
                      const updatedFilters = map(selectedFilters, (f) =>
                        f.value === filterItem.value
                          ? {
                              ...f,
                              isActive: values.length > 0,
                              selectedValues: values,
                            }
                          : f,
                      );
                      handleFilterChange(updatedFilters);
                    }}
                  >
                    {buckets.flatMap((bucket, bucketIdx) => {
                      const items = bucket.options.map((option) => {
                        const isSelected = currentValues.includes(option.value);
                        return (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            dense
                            sx={{ py: 0.25, pl: 1.5 }}
                          >
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0.5, mr: 0.5 }}
                            />
                            <ListItemText
                              primary={option.label}
                              slotProps={{
                                primary: { fontSize: '0.875rem' },
                              }}
                            />
                          </MenuItem>
                        );
                      });

                      if (!bucket.name) return items;

                      const groupValues = bucket.options.map((o) => o.value);
                      const selectedInGroup = groupValues.filter((v) =>
                        currentValues.includes(v),
                      ).length;
                      const allSelected =
                        selectedInGroup === groupValues.length &&
                        groupValues.length > 0;
                      const someSelected = selectedInGroup > 0 && !allSelected;

                      return [
                        <MenuItem
                          key={`__header__${bucket.name}`}
                          value={`${GROUP_MARKER}${bucket.name}`}
                          dense
                          sx={{
                            py: 0.5,
                            pl: 1.5,
                            mt: bucketIdx === 0 ? 0 : 1,
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            bgcolor: 'action.hover',
                            borderBottom: 1,
                            borderTop: 1,
                            borderColor: 'divider',
                            '&:hover': { fontWeight: 'bold' },
                          }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            size="small"
                            sx={{
                              p: 0.5,
                              mr: 0.5,
                              '& .MuiSvgIcon-root': { fontSize: 22 },
                            }}
                          />
                          <ListItemText
                            primary={bucket.name}
                            slotProps={{
                              primary: {
                                variant: 'overline',
                                color: 'text.secondary',
                                lineHeight: 1.5,
                              },
                            }}
                          />
                        </MenuItem>,
                        ...items,
                      ];
                    })}
                  </Select>
                </FormControl>
              </Box>
            );
          }

          if (filterItem.type === 'date-range') {
            const dateFrom = selectedFilter?.selectedDateFrom || '';
            const dateTo = selectedFilter?.selectedDateTo || '';

            const updateDateRange = (nextFrom: string, nextTo: string) => {
              const updatedFilters = map(selectedFilters, (f) =>
                f.value === filterItem.value
                  ? {
                      ...f,
                      isActive: !!nextFrom || !!nextTo,
                      selectedDateFrom: nextFrom || undefined,
                      selectedDateTo: nextTo || undefined,
                    }
                  : f,
              );
              handleFilterChange(updatedFilters);
            };

            return (
              <Box key={filterItem.value}>
                <Typography variant="caption" color="text.secondary">
                  {filterItem.label}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="From"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={dateFrom}
                    onChange={(e) => updateDateRange(e.target.value, dateTo)}
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="To"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={dateTo}
                    onChange={(e) => updateDateRange(dateFrom, e.target.value)}
                  />
                </Stack>
              </Box>
            );
          }

          if (filterItem.type === 'date-field-range') {
            return (
              <DateFieldRangeFilter
                key={filterItem.value}
                filterItem={filterItem}
                selectedFilter={selectedFilter}
                onChange={(next) => {
                  const updatedFilters = map(selectedFilters, (f) =>
                    f.value === filterItem.value ? { ...f, ...next } : f,
                  );
                  handleFilterChange(updatedFilters);
                }}
              />
            );
          }

          if (filterItem.type === 'custom') {
            return (
              <CustomFilter
                key={filterItem.value}
                filterItem={filterItem}
                selectedFilter={selectedFilter}
                onChange={(next) => {
                  const updatedFilters = map(selectedFilters, (f) =>
                    f.value === filterItem.value ? { ...f, ...next } : f,
                  );
                  handleFilterChange(updatedFilters);
                }}
              />
            );
          }

          if (filterItem.type === 'checkbox') {
            return (
              <Box
                key={filterItem.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  checked={selectedFilter?.isActive || false}
                  onChange={(event) => {
                    const updatedFilters = map(selectedFilters, (f) =>
                      f.value === filterItem.value
                        ? { ...f, isActive: event.target.checked }
                        : f,
                    );
                    handleFilterChange(updatedFilters);
                  }}
                  size="small"
                />
                <Typography variant="body2">{filterItem.label}</Typography>
              </Box>
            );
          }

          return null;
        })}
        {/* Sort goes last: the filters decide WHAT is listed, which is what
              people open this for; order is the afterthought. */}
        {sortInPanel && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {renderSortControl(true)}
          </Stack>
        )}
        {refreshInPanel && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            isLoading={isFetching}
            muiIcon={<Refresh fontSize="small" />}
            content="Refresh"
          />
        )}
      </Box>
    );

    // On a phone the panel floats over the list from the filter button. Inline,
    // it pushed the list almost entirely off a small screen while open.
    if (filtersInPopover) {
      return (
        <Popover
          open={showFilters}
          anchorEl={filterButtonRef.current}
          onClose={() => setShowFilters(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          // Lets the panel sit 8px from the screen edges, in line with the
          // page gutter, rather than Popover's default 16px.
          marginThreshold={8}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                p: 1.5,
                pt: 2,
                boxSizing: 'border-box',
                // The full width of a phone screen, gutter to gutter, so the
                // fields are as wide as the search box above them. Capped so
                // it doesn't stretch across a tablet. `maxWidth` has to be
                // stated: Popover's own cap is 32px narrower than the screen.
                width: 'calc(100vw - 16px)',
                maxWidth: 480,
                maxHeight: '70dvh',
              },
            },
          }}
        >
          {panel}
        </Popover>
      );
    }

    return <Collapse in={persistentFilters || showFilters}>{panel}</Collapse>;
  };

  const itemCount = accessor?.pageInfo?.itemCount ?? 0;

  // Only worth offering when the filters are demonstrably hiding matches:
  // `itemCount` is the total the current filters match (not the page size), so
  // a filter set that already contains every match says nothing here. While a
  // query is in flight `itemCount` still describes the PREVIOUS result, which
  // would make that comparison lie, so hold the prompt back until it lands.
  // A page whose every tab spells out a preset has no unfiltered state to
  // clear to — see `neutralTab`. Offering the prompt there would promise a
  // result set the page cannot produce.
  const canClearToUnfiltered = tabs.length === 0 || neutralTab != null;

  const showUnfilteredPrompt =
    !!searchValue &&
    activeFilterCount > 0 &&
    unfilteredCount != null &&
    !operation.loading &&
    canClearToUnfiltered &&
    // The count is per search TERM, and `searchValue` moves on every keystroke
    // while the query behind it is still debounced. Without this the banner
    // renders the previous term's count against the new term's text —
    // `7 results for "smithx"` using the answer for `"smith"` — and compares it
    // against an `itemCount` from the older query. `!operation.loading` only
    // covers the in-flight window, not the debounce that precedes it.
    countedTermRef.current === searchValue &&
    unfilteredCount > itemCount;

  const selectedRows = useMemo(
    () =>
      selectable
        ? ((accessor?.items ?? []) as TData[]).filter((r) =>
            selectedIds.has(getRowId(r)),
          )
        : [],
    [selectable, accessor?.items, selectedIds, getRowId],
  );

  const showPreview = renderPreview && selectedRow;

  // Under page scroll the preview is as tall as its content and the document
  // scrolls it, so it opens wherever the list had been scrolled to — header
  // and Close above the screen from far enough down. Open it at its top, and
  // put the list back where it was on the way out.
  const previewOpen = !!showPreview;
  useLayoutEffect(() => {
    if (pageScroll && previewOpen) {
      scrollPageToTop();
    } else if (pageScroll && listScrollRef.current !== null) {
      window.scrollTo({ top: listScrollRef.current });
      listScrollRef.current = null;
    }
  }, [previewOpen, pageScroll, scrollPageToTop]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        ...(pageScroll
          ? { minHeight: resolvedHeight }
          : { height: resolvedHeight }),
        mt: 1,
        // For `scrollPageToTop`: clear of what is pinned above.
        scrollMarginTop:
          'var(--page-sticky-top, var(--app-header-height, 0px))',
      }}
    >
      {/* Main search page */}
      <SearchPageCard
        height="fill"
        pageScroll={pageScroll}
        sx={{
          minWidth: 0,
          width: 1,
          maxWidth: showPreview
            ? { xs: '100%', md: 'calc(66.67% - 8px)' }
            : '100%',
          transition: 'max-width 0.15s linear',
          // On a phone the preview takes the page instead of sharing it: the
          // container is a fixed-height column there, so list and preview were
          // both squeezed into it and the preview spilled over the list. The
          // preview header's own Close is the way back.
          display: {
            xs: showPreview ? 'none' : 'flex',
            md: 'flex',
          },
        }}
      >
        {/* On a phone the title and action buttons take a good part of the
            screen before a single row is drawn, and how much varies page to
            page. They slide away while the list is scrolled down, and return
            only when the user pulls down past the top of the list — not on
            scrolling back up (see useCollapseOnScroll). Search, filters, tabs
            and column headings stay put. */}
        {header && (
          <SearchPageHeader
            title={header.title}
            subtitle={header.subtitle}
            actions={header.actions}
            collapsed={headerCollapse.collapsed}
          />
        )}

        {/* Under page scroll the title band can be left half off the top of
            the screen. Two snap points stop that: the top of the page, and
            this marker, where the band is exactly gone and the search bar
            sits under the app header. `proximity`, so a scroll only settles
            on one when it ends near it — the list below scrolls freely. */}
        {pageScroll && header && (
          <>
            <GlobalStyles
              styles={{
                html: { scrollSnapType: 'y proximity' },
                body: { scrollSnapAlign: 'start' },
              }}
            />
            <Box
              sx={{
                flexShrink: 0,
                height: 0,
                scrollSnapAlign: 'start',
                scrollMarginTop:
                  'var(--page-sticky-top, var(--app-header-height, 0px))',
              }}
            />
          </>
        )}

        {/* Search bar and tabs. Under page scroll they pin as one block just
            below the app header; otherwise this wrapper dissolves and they
            are rows of the card like the rest. */}
        <Box
          sx={
            pageScroll
              ? {
                  position: 'sticky',
                  // Under whatever the page has already pinned above — a
                  // detail page's header and tabs — or else the app header.
                  top: 'var(--page-sticky-top, var(--app-header-height, 0px))',
                  zIndex: 3,
                  bgcolor: 'background.paper',
                }
              : { display: 'contents' }
          }
        >
          {/* Search bar */}
          <SearchPageToolbar>
            {/* One row at every width. On a phone the sort control moves into
              the filter panel, and refresh with it, which leaves room for
              columns and filters beside the search box instead of on a row of
              their own. */}
            <Stack
              direction="row"
              spacing={{ xs: 0.5, md: 1.5 }}
              alignItems="center"
            >
              {/* Search input */}
              <TextField
                fullWidth
                size="small"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        {/* On a phone the header toggle takes the search
                          icon's place — the tap equivalent of scrolling the
                          header away and pulling it back. The placeholder
                          already says this is a search box. */}
                        {isMobile && header && !pageScroll ? (
                          <HeaderCollapseToggle
                            collapsed={headerCollapse.collapsed}
                            onToggle={headerCollapse.toggle}
                          />
                        ) : (
                          <Search
                            sx={{ color: 'text.disabled', fontSize: 20 }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                  },
                }}
              />

              {/* Controls */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                flexShrink={0}
              >
                {/* Sort — on a phone it lives in the filter panel instead. */}
                {!hideSort && !sortInPanel && renderSortControl(false)}

                {/* Refresh — on a phone the row is short of room, so it lives
                  in the filter panel with sort. */}
                {!refreshInPanel && (
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} size="small">
                      {isFetching ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Refresh fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Manage Columns">
                  <IconButton onClick={handleColumnMenuOpen} size="small">
                    <ViewColumn fontSize="small" />
                  </IconButton>
                </Tooltip>

                {showFilterButton && (
                  <Tooltip
                    title={
                      activeFilterSummary.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              mb: 0.25,
                            }}
                          >
                            Active filters
                          </Typography>
                          {activeFilterSummary.map((line) => (
                            <Typography
                              key={line}
                              variant="caption"
                              sx={{ display: 'block' }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        filterButtonLabel
                      )
                    }
                  >
                    <IconButton
                      ref={filterButtonRef}
                      onClick={() => setShowFilters(!showFilters)}
                      size="small"
                      // The tooltip is a node now, so MUI no longer derives an
                      // aria-label from it — name the button ourselves.
                      aria-label={
                        activeFilterCount > 0
                          ? `${filterButtonLabel} (${activeFilterCount} active)`
                          : filterButtonLabel
                      }
                      color={
                        showFilters || activeFilterCount > 0
                          ? 'primary'
                          : 'default'
                      }
                      sx={{ position: 'relative' }}
                    >
                      <FilterList fontSize="small" />
                      {activeFilterCount > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: '50%',
                            minWidth: 14,
                            height: 14,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.625rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {activeFilterCount}
                        </Box>
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Column menu */}
            <Menu
              anchorEl={columnMenuAnchor}
              open={Boolean(columnMenuAnchor)}
              onClose={handleColumnMenuClose}
              slotProps={{ paper: { sx: { maxHeight: 300 } } }}
            >
              {columns.map((column) =>
                column.label ? (
                  <MenuItem
                    key={column.label}
                    onClick={() => handleColumnToggle(column.label!)}
                    dense
                  >
                    <Checkbox
                      checked={selectedColumns.includes(column.label)}
                      size="small"
                    />
                    <ListItemText primary={column.label} />
                  </MenuItem>
                ) : null,
              )}
            </Menu>

            {/* Filters */}
            {renderFilterBar()}

            {/* "Your filters are hiding matches" prompt */}
            {showUnfilteredPrompt && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  mt: 1.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                <Search sx={{ fontSize: 18, color: 'info.main' }} />
                {/* Takes the slack so the action sits at the far right. */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexGrow: 1 }}
                >
                  {unfilteredCount} result{unfilteredCount === 1 ? '' : 's'} for
                  &ldquo;{searchValue}&rdquo; without filters
                </Typography>
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  onClick={clearAllFilters}
                  content="Search without filters"
                  sx={{ flexShrink: 0, fontWeight: 600 }}
                />
              </Stack>
            )}
          </SearchPageToolbar>

          {/* Tabs — preset filter views, sitting directly above the results so
            they read as what's being listed rather than as page navigation. */}
          {tabs.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tabs
                value={activeTab ?? false}
                onChange={(_event, value) => handleTabChange(value as string)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 34,
                  '& .MuiTabs-indicator': { height: 2 },
                  '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
                }}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={tab.icon}
                    iconPosition="start"
                    label={
                      tab.badge != null ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <span>{tab.label}</span>
                          <Chip
                            label={tab.badge}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.6875rem',
                              '& .MuiChip-label': { px: 0.625 },
                            }}
                          />
                        </Stack>
                      ) : (
                        tab.label
                      )
                    }
                    sx={{
                      minHeight: 34,
                      py: 0.5,
                      px: 1.5,
                      minWidth: 0,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )}
        </Box>

        {/* Results */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            // Anchor for the floating selection bar below.
            position: 'relative',
          }}
        >
          {operation.error && (
            <Box sx={{ flexShrink: 0, p: 3 }}>
              <Typography color="error">
                Error loading results: {operation.error.message}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          {!hideButtons && actionButtons && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexShrink: 0, px: 3, pt: 2 }}
            >
              {map(actionButtons(operation), (buttonProps, index) => (
                <Button key={index} {...buttonProps} />
              ))}
            </Stack>
          )}

          {/* Selection action bar — floats over the results, pinned
              bottom-center above the footer, so selecting rows doesn't shove
              the list down. */}
          {selectable && renderSelectionActions && selectedIds.size > 0 && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                // Under page scroll the results are as tall as the list, so
                // the bar holds to the screen instead of to their far end.
                position: pageScroll ? 'fixed' : 'absolute',
                bottom: pageScroll
                  ? 'calc(72px + env(safe-area-inset-bottom, 0px))'
                  : 72,
                // Edge to edge on a phone. Centred from `left: 50%` the bar
                // can only shrink-wrap into the right half of the screen, so
                // its count and buttons wrapped onto two and three lines.
                // Fixed to the screen it also has to clear the notch itself
                // (either side, by rotation); inside the card the shell's
                // padding already has.
                left: {
                  xs: pageScroll
                    ? 'calc(8px + env(safe-area-inset-left, 0px))'
                    : 8,
                  sm: '50%',
                },
                right: {
                  xs: pageScroll
                    ? 'calc(8px + env(safe-area-inset-right, 0px))'
                    : 8,
                  sm: 'auto',
                },
                zIndex: 5,
                maxWidth: { sm: 'calc(100% - 32px)' },
                px: { xs: 1.5, sm: 2.5 },
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 6,
                border: 1,
                borderColor: 'divider',
                '@keyframes selectionBarRise': {
                  from: { opacity: 0, transform: 'translate(-50%, 8px)' },
                  to: { opacity: 1, transform: 'translate(-50%, 0)' },
                },
                // No horizontal offset to hold on a phone, where the bar is
                // pinned by both edges.
                '@keyframes selectionBarRiseFull': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: {
                  xs: 'selectionBarRiseFull 0.15s ease both',
                  sm: 'selectionBarRise 0.15s ease both',
                },
              }}
            >
              <Tooltip title="Clear selection">
                <IconButton size="small" onClick={clearSelection}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {selectedIds.size} selected
              </Typography>
              {/* The actions take the rest of the row on a phone, so they sit
                  against the right edge rather than bunched by the count. */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flex: { xs: 1, sm: 'none' },
                  minWidth: 0,
                }}
              >
                {renderSelectionActions(selectedRows, clearSelection)}
              </Box>
            </Stack>
          )}

          {/* Scrollable records */}
          <SearchPageBody
            collapse={headerCollapse}
            sx={{
              // A card is its own outline. The table's hairline under each
              // row drew a second, stray rule in the gap between two cards.
              ...(cardRows && {
                '& td.MuiTableCell-root': { borderBottom: 0 },
              }),
              // The rows are still table rows — the body is re-flowed as a
              // grid of them, which keeps loading, row animation and preview
              // selection exactly as the table already does them.
              ...(cardRows &&
                cardGridMinWidth &&
                !selectable &&
                !renderSubComponent && {
                  '& table': { display: 'block' },
                  '& tbody': {
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(min(${cardGridMinWidth}px, 100%), 1fr))`,
                    px: 0.75,
                  },
                  // Flex, so the one cell stretches to the row's height and
                  // cards in a row finish level.
                  '& tbody > tr': { display: 'flex', minWidth: 0 },
                  // No row hover: in a grid the "row" is one cell, so the
                  // tint drew a grey box around a single card, wider than
                  // the card and reading as a selection. The card has its
                  // own hover.
                  '& tbody > tr.MuiTableRow-root:hover': {
                    backgroundColor: 'transparent',
                  },
                  // Half the gap a side, so cards sit a gap apart and the
                  // grid's outer edge lines up with the toolbar above it
                  // rather than being inset by a table cell's padding.
                  '& tbody > tr > td.MuiTableCell-root': {
                    display: 'block',
                    flex: 1,
                    minWidth: 0,
                    px: 0.75,
                    py: 0.75,
                  },
                }),
            }}
          >
            <Table<TData, TQuery, TVars>
              rows={accessor?.items || []}
              columns={filteredColumns.map((col) => ({
                ...col,
                sortable: false,
              }))}
              loading={operation.loading || !operation.called}
              nowrap
              animateRows
              striped={!cardRows}
              hideHeader={cardRows}
              selectable={selectable}
              selectedIds={selectedIds}
              onToggleRowSelection={toggleRowSelection}
              onToggleAllSelection={toggleAllSelection}
              getRowId={getRowId}
              isRowSelectable={isRowSelectable}
              onSelect={renderPreview ? setPreviewRow : undefined}
              renderSubComponent={
                renderSubComponent
                  ? (row) => renderSubComponent(row, operation)
                  : undefined
              }
            />

            {/* No Results */}
            {!operation.loading &&
              accessor &&
              (!accessor.items || accessor.items.length === 0) && (
                <Box sx={{ py: 6 }}>
                  <NoResultsText textOverride={noResultsOverride} />
                </Box>
              )}
          </SearchPageBody>

          {/* Footer: count + pagination */}
          {(pageCount > 1 || itemCount > 0) && (
            <SearchPageFooter
              label={`${itemCount.toLocaleString()} result${itemCount !== 1 ? 's' : ''}`}
            >
              <Pagination
                currentPage={page}
                pageCount={pageCount}
                setPage={handlePageChange}
                showSinglePage
              />
            </SearchPageFooter>
          )}
        </Box>
      </SearchPageCard>

      {/* Preview panel */}
      {renderPreview &&
        (() => {
          const config = selectedRow ? previewConfig?.(selectedRow) : null;
          return (
            <Box
              sx={{
                width: showPreview
                  ? { xs: '100%', md: 'calc(33.33% - 8px)' }
                  : 0,
                flexShrink: 0,
                minWidth: 0,
                minHeight: 0,
                // Full height on mobile, not `auto`: the list is hidden while
                // this is open, so it owns the column.
                height: showPreview ? '100%' : 0,
                overflow: showPreview ? 'auto' : 'hidden',
                transition: 'width 0.15s linear',
                visibility: showPreview ? 'visible' : 'hidden',
                bgcolor: 'background.paper',
                // Square on a phone, where it spans the viewport.
                borderRadius: { xs: 0, md: 2 },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                key={previewKey}
                sx={{
                  '@keyframes previewContentFadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                  animation: 'previewContentFadeIn 0.15s linear',
                }}
              >
                {/* Preview header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    px: { xs: 2, md: 3 },
                    py: 2,
                    bgcolor: 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    >
                      {config?.title}
                    </Typography>
                    {config?.status && (
                      <Typography variant="body2" color="success.main">
                        {config.status}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    {config?.href && (
                      <Tooltip title="Open in new tab">
                        <IconButton
                          size="small"
                          component="a"
                          href={config.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNew sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Close">
                      <IconButton
                        size="small"
                        onClick={() => setPreviewRow(null)}
                      >
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {config?.action && (
                  <Box sx={{ px: 2.5, pt: 1.5, '& > *': { width: '100%' } }}>
                    {config.action}
                  </Box>
                )}

                {/* Preview content */}
                <Box sx={config?.flush ? undefined : { p: 2.5 }}>
                  {selectedRow &&
                    renderPreview(selectedRow, () => setPreviewRow(null))}
                </Box>
              </Box>
            </Box>
          );
        })()}
    </Box>
  );
}
